import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { reviews } from "@/mocks/reviews";
import useDocumentMeta from "@/hooks/useDocumentMeta";

type ResultType = "episode" | "character" | "review";

interface SearchResult {
  id: string;
  type: ResultType;
  title: string;
  summary: string;
  to: string;
  meta: string;
}

const typeLabels: Record<ResultType, string> = {
  episode: "剧情简介",
  character: "角色信息",
  review: "剧评",
};

const tabs: { key: "all" | ResultType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "episode", label: "剧情简介" },
  { key: "character", label: "角色信息" },
  { key: "review", label: "剧评" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [activeTab, setActiveTab] = useState<"all" | ResultType>("all");

  useEffect(() => {
    setInput(query);
    setActiveTab("all");
  }, [query]);

  useDocumentMeta({
    title: query
      ? `搜索“${query}” - 美国式禁忌2站内搜索`
      : "站内搜索 - 美国式禁忌2",
    description:
      "在《美国式禁忌2》影视内容站内搜索剧情解析、角色信息与观众剧评，快速找到你关心的内容。",
    keywords: "美国式禁忌2搜索,美国式禁忌2剧情查找,美国式禁忌2角色查找",
  });

  const results = useMemo<SearchResult[]>(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    const matched: SearchResult[] = [];

    episodes.forEach((episode) => {
      const haystack = [
        episode.title,
        episode.summary,
        episode.tagline,
        episode.tags.join(" "),
        episode.cast.join(" "),
        episode.characters.join(" "),
        `第${episode.number}集`,
      ]
        .join(" ")
        .toLowerCase();
      if (haystack.includes(keyword)) {
        matched.push({
          id: episode.id,
          type: "episode",
          title: `第 ${episode.number} 集 · ${episode.title}`,
          summary: episode.summary,
          to: `/detail/${episode.id}`,
          meta: `${episode.date} · ${episode.tags.join(" / ")}`,
        });
      }
    });

    characters.forEach((character) => {
      const haystack = [
        character.name,
        character.actor,
        character.role,
        character.description,
        character.traits.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      if (haystack.includes(keyword)) {
        matched.push({
          id: character.id,
          type: "character",
          title: character.name,
          summary: character.description,
          to: `/detail/${character.appearsIn[0]}`,
          meta: `${character.actor} 饰 · ${character.role}`,
        });
      }
    });

    reviews.forEach((review) => {
      const haystack = [review.author, review.content].join(" ").toLowerCase();
      if (haystack.includes(keyword)) {
        const episode = episodes.find((item) => item.id === review.episodeId);
        matched.push({
          id: review.id,
          type: "review",
          title: `${review.author} 的点评`,
          summary: review.content,
          to: `/detail/${review.episodeId}`,
          meta: episode ? `关于 第 ${episode.number} 集 ${episode.title}` : "观众点评",
        });
      }
    });

    return matched;
  }, [query]);

  const filtered = useMemo(
    () => (activeTab === "all" ? results : results.filter((item) => item.type === activeTab)),
    [results, activeTab]
  );

  const recommendations = useMemo(() => episodes.slice(0, 3), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <SiteLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />

        <div className="mt-6 mb-8">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            站内搜索
          </h1>
          <p className="mt-3 text-sm text-foreground-600 max-w-2xl">
            搜索基于本站演示内容的前端过滤，无后端数据库接口，结果实时展示。
          </p>
        </div>

        <form
          onSubmit={submit}
          className="flex flex-col sm:flex-row gap-3 max-w-3xl mb-8"
        >
          <div className="flex-1 flex items-center h-12 px-4 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-500 transition-colors">
            <i className="ri-search-line text-foreground-500 text-lg" />
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入关键词，如“凯文”“第 9 集”“对峙”"
              aria-label="搜索关键词"
              className="flex-1 bg-transparent outline-none border-none text-base text-foreground-900 placeholder:text-foreground-500 px-3"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            重新搜索
          </button>
        </form>

        {query.trim() ? (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {tabs.map((tab) => {
                const count =
                  tab.key === "all"
                    ? results.length
                    : results.filter((item) => item.type === tab.key).length;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`h-9 px-4 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      activeTab === tab.key
                        ? "bg-accent-500 text-background-50 font-medium"
                        : "bg-background-100 border border-background-200 text-foreground-700 hover:text-accent-300"
                    }`}
                  >
                    {tab.label}
                    <span className="ml-1.5 opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-foreground-600 mb-5">
              关键词“<span className="text-accent-400 font-medium">{query}</span>”共找到{" "}
              <span className="text-accent-400 font-medium">{filtered.length}</span> 条结果
            </p>

            {filtered.length > 0 ? (
              <div className="flex flex-col gap-4 max-w-4xl">
                {filtered.map((item) => (
                  <Link
                    key={`${item.type}-${item.id}`}
                    to={item.to}
                    className="group rounded-lg bg-background-100 border border-background-200 hover:border-primary-600 p-5 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className={`inline-flex items-center h-6 px-2.5 rounded-full text-xs whitespace-nowrap ${
                          item.type === "episode"
                            ? "bg-primary-600/20 text-primary-300"
                            : item.type === "character"
                              ? "bg-accent-500/15 text-accent-400"
                              : "bg-secondary-100/10 text-secondary-300"
                        }`}
                      >
                        {typeLabels[item.type]}
                      </span>
                      <span className="text-xs text-foreground-500 truncate">{item.meta}</span>
                    </div>
                    <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-accent-300 transition-colors">
                      {item.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {item.summary}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="未找到符合条件的内容"
                description="换个关键词试试，或浏览下面推荐的热门剧集解析。"
              />
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="你可以搜索剧集标题、剧情关键词、角色名或剧评内容。"
          />
        )}

        <section className="mt-14">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2 mb-5">
            <span className="w-1.5 h-5 rounded-full bg-accent-500" />
            热门剧集推荐
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {recommendations.map((episode) => (
              <Link
                key={episode.id}
                to={`/detail/${episode.id}`}
                className="group rounded-lg bg-background-100 border border-background-200 hover:border-primary-600 p-5 transition-colors cursor-pointer"
              >
                <span className="text-xs text-accent-400">第 {episode.number} 集</span>
                <h3 className="mt-1.5 font-heading text-base font-bold text-foreground-950 group-hover:text-accent-300 transition-colors">
                  {episode.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {episode.summary}
                </p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}